import { memo, useCallback, useEffect } from 'react';
import { useNavigation } from '@bonanza-social/navigation';
import {
  Box,
  WebView,
  PressableIcon,
  useSafeAreaInsets,
} from '@bonanza-social/moonlight';
import { Platform } from 'react-native';
import { MetaGameProps } from './MetaGame.types';
import MetaGameLoading from '../MetaGameLoading/MetaGameLoading';
import { useMetaGameLaunchUrl } from '../../hooks';
import { useDispatch } from '@bonanza-social/toolkit/state';
import { dailyMetaGameCompleted, instantMetaGameCompleted } from '../../state';

function MetaGame({ gameType }: MetaGameProps) {
  const navigation = useNavigation();
  const { top, bottom } = useSafeAreaInsets();
  const launchUrl = useMetaGameLaunchUrl(gameType);
  const dispatch = useDispatch();

  const onClose = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleMetaGameClose = useCallback(() => {
    switch (gameType) {
      case 'DailyScratcher':
      case 'DailyWheel':
        dispatch(dailyMetaGameCompleted(gameType));
        return;
      case 'Scratcher':
      case 'Wheel':
        dispatch(instantMetaGameCompleted());
        return;
      default:
        return;
    }
  }, [dispatch, gameType]);

  useEffect(() => {
    return handleMetaGameClose;
  }, [handleMetaGameClose]);

  if (!launchUrl) {
    return <MetaGameLoading />;
  }

  return (
    <Box flex={1}>
      <PressableIcon
        position="absolute"
        name="close"
        width="space-600"
        height="space-600"
        gradient="accentBackground"
        backgroundColor="transparent"
        top={Platform.select({
          ios: 'space-1800',
          web: 'space-400',
          android: 'space-1800',
        })}
        marginRight={Platform.select({
          ios: 'space-400',
          web: 'space-200',
          android: 'space-400',
        })}
        alignSelf="flex-end"
        zIndex={10}
        onPress={onClose}
      />
      <WebView
        domStorageEnabled
        javaScriptEnabled
        sharedCookiesEnabled
        cacheEnabled={!__DEV__}
        webviewDebuggingEnabled={__DEV__}
        originWhitelist={['*']}
        source={{ uri: launchUrl }}
        injectedJavaScript={`
          document.documentElement.style.setProperty("--safe-area-inset-top", "${top}px");
          document.documentElement.style.setProperty("--safe-area-inset-bottom", "${bottom}px");
        `}
      />
    </Box>
  );
}

export default memo(MetaGame);
